// LES TENUES DU BAROUDEUR (26/07, demande Claude-Alain « des loots qui changent le skin »).
//
// Le héros n'est pas un sprite figé : client/baroudeur.ts le peint lettre par lettre depuis une
// palette (chaque lettre = un ton de son costume). Une TENUE n'est donc pas un nouveau dessin, mais
// un nouveau JEU DE COULEURS — net à toute taille, sans regénérer runners.png, et compatible avec
// toutes les poses existantes, dash et échelle comprises.
//
// Module PUR : ni DOM, ni canvas, ni simulation. Rien ici n'entre dans le hash d'état.
//
// ⚠️ La TEINTE gratuite (les 7 couleurs de shared/skins) continue de s'appliquer PAR-DESSUS la
// tenue équipée : une combinaison de glace en teinte verte est une combinaison verte. C'est ce qui
// garde la promesse « changer les couleurs, gratuitement », et ça multiplie les combinaisons.

/** Les lettres de palette qui peignent le costume (miroir de OUTFIT_LETTERS de baroudeur.ts). */
export const OUTFIT_LETTERS = ['S', 'o', 't', 'w', 'u', 'p', 'q', 'r'] as const
export type OutfitLetter = (typeof OUTFIT_LETTERS)[number]

/**
 * Quel TON (0 = le plus sombre, 4 = le plus clair) reçoit chaque lettre. Cette table encode le
 * relief du personnage : les creux en 0, les arêtes éclairées en 4. La respecter, c'est garder un
 * héros lisible quelle que soit la tenue ; l'inverser, c'est un héros en négatif.
 */
export const TON_PAR_LETTRE: Record<OutfitLetter, 0 | 1 | 2 | 3 | 4> = {
  S: 0, o: 0, t: 0,   // creux : dessous des manches, ombre du sac
  w: 1,               // pénombre
  u: 2, p: 2,         // ton moyen : le gros du tissu
  q: 3,               // lumière
  r: 4,               // reflet vif : arêtes et plis éclairés
}

export interface Outfit {
  /** Identifiant, aussi celui du loot équipable (slot 'outfit'). */
  id: string
  name: string
  /** Une phrase pour le vestiaire. */
  desc: string
  /** Cinq tons du plus SOMBRE au plus CLAIR. */
  tons: readonly [string, string, string, string, string]
  /** Retouches hors costume : casque (d/D), écharpe et lunettes (e/l/m/n), bottes (K/L/M),
   *  pantalon (a/b/c). Absent = la pièce garde sa couleur d'origine. */
  extra?: Readonly<Record<string, string>>
}

// Les huit tenues. Chaque famille de couleur raconte un endroit de la mine ou un métier ;
// aucune n'est une simple variation de teinte de la veste ocre (ça, c'est déjà gratuit).
export const OUTFITS: readonly Outfit[] = [
  {
    id: 'tenue-charbon', name: 'Tenue de charbon',
    desc: 'La tenue du fond, noircie par le travail.',
    tons: ['#14120f', '#221e18', '#332d24', '#4a4136', '#6b5f4e'],
    extra: { d: '#3a352c', D: '#5c5448' }, // casque terni
  },
  {
    id: 'tenue-camouflage', name: 'Tenue de camouflage',
    desc: 'Kaki et olive : on ne te voit plus venir.',
    tons: ['#1e2a16', '#2f3f20', '#44562c', '#5d7139', '#87a052'],
    extra: { K: '#2a2f1c', L: '#3d4429', M: '#575f38' }, // bottes assorties
  },
  {
    id: 'tenue-glace', name: 'Combinaison de glace',
    desc: 'Bleu polaire et givre blanc, pour les galeries froides.',
    tons: ['#1b3a52', '#265575', '#3878a0', '#6fb4d8', '#d8f2ff'],
    extra: { d: '#cfe6f5', D: '#ffffff', e: '#a8e6ff', n: '#e8faff' }, // casque givré, écharpe pâle
  },
  {
    id: 'tenue-abysse', name: 'Tenue des abysses',
    desc: 'Bleu nuit et turquoise, comme l’eau des profondeurs.',
    tons: ['#0d1c2e', '#152c46', '#1e4665', '#2d7a94', '#57d3c8'],
    extra: { e: '#57d3c8', l: '#12464a', m: '#1e7d78', n: '#8ff0e4' },
  },
  {
    id: 'tenue-lave', name: 'Tenue de lave',
    desc: 'Noire, parcourue de coulées incandescentes.',
    tons: ['#1a0c08', '#33130c', '#61200f', '#a83c14', '#ffb347'],
    extra: { d: '#4a2418', D: '#7a3c22', e: '#ff8f2a', n: '#ffd23f' },
  },
  {
    id: 'tenue-scaphandre', name: 'Scaphandre de cuivre',
    desc: 'Laiton rivé et hublot doré, sortie tout droit de la forge.',
    tons: ['#2e1d10', '#4d3218', '#7a5324', '#b8862f', '#f5d99a'],
    extra: { d: '#c9922f', D: '#ffe9a8', e: '#8ad4e0', l: '#26555c', m: '#3f8f96', n: '#c8f2f7' },
  },
  {
    id: 'tenue-neon', name: 'Tenue néon',
    desc: 'Violet électrique et cyan : impossible à rater dans le noir.',
    tons: ['#1a0e33', '#3a1470', '#6a1fb0', '#b44ff0', '#f0c8ff'],
    extra: { d: '#2a1450', D: '#6afcff', e: '#6afcff', n: '#d8feff', K: '#1a0e33', L: '#3a1470', M: '#6a1fb0' },
  },
  {
    id: 'tenue-or', name: 'Tenue d\'or massif',
    desc: 'Le costume que la Mine finit par offrir à ceux qui creusent.',
    tons: ['#4a3208', '#7a5510', '#b8871c', '#ecc33a', '#fff6c8'],
    extra: { d: '#ecc33a', D: '#fff6c8', K: '#4a3208', L: '#7a5510', M: '#b8871c' },
  },

  // --- SECONDE FOURNÉE (26/07, « ajoute encore des skins pour le personnage ») ------------------
  // Règle qu'on se donne pour ne pas empiler des variantes : chaque tenue doit se reconnaître à sa
  // FAMILLE DE COULEUR, à distance, sans lire son nom — et se servir des retouches (casque,
  // écharpe, bottes, pantalon) pour être un costume, pas un lavis d'une seule teinte.
  {
    id: 'tenue-rouille', name: 'Tenue de rouille',
    desc: 'Le fer laissé trop longtemps au fond, mangé d’orange.',
    tons: ['#241209', '#3d1e0d', '#6b3315', '#a75a24', '#d9975a'],
    extra: { d: '#6b3315', D: '#a75a24', K: '#241209', L: '#3d1e0d', M: '#6b3315', e: '#8a4a1e' },
  },
  {
    id: 'tenue-ardoise', name: 'Tenue d\'ardoise',
    desc: 'Gris-bleu de pierre fendue, sobre et froide.',
    tons: ['#12171f', '#1e2733', '#33414f', '#56687a', '#93a7b8'],
    extra: { d: '#33414f', D: '#56687a', e: '#93a7b8', n: '#d4e0e8', a: '#1a222c', b: '#28323e', c: '#3c4a58' },
  },
  {
    id: 'tenue-mousse', name: 'Tenue de mousse',
    desc: 'Vert d’humidité et d’écorce : la galerie qui reverdit.',
    tons: ['#12180d', '#1f2c14', '#33481f', '#4e6b2c', '#82a555'],
    extra: { d: '#4a3a22', D: '#6d5636', K: '#241a10', L: '#3a2a1a', M: '#4f3b24', e: '#82a555' },
  },
  {
    id: 'tenue-porcelaine', name: 'Tenue de porcelaine',
    desc: 'Blanc laiteux et bleu peint à la main. Fragile, en apparence.',
    tons: ['#1c2c46', '#2f4a72', '#5878a8', '#a8c4de', '#f2f6fb'],
    extra: { d: '#f2f6fb', D: '#ffffff', e: '#2f4a72', l: '#1c2c46', m: '#3f5f8c', n: '#a8c4de', K: '#1c2c46', L: '#2f4a72', M: '#5878a8' },
  },
  {
    id: 'tenue-braise', name: 'Tenue de braise',
    desc: 'Cendre grise que le moindre courant d’air rallume.',
    tons: ['#161412', '#282521', '#403a33', '#6b6055', '#a8998a'],
    extra: { d: '#403a33', D: '#6b6055', e: '#ff7a1e', l: '#7a2c06', m: '#c24f0e', n: '#ffb055' },
  },
  {
    id: 'tenue-pourpre', name: 'Tenue de velours pourpre',
    desc: 'Le pourpre profond des grandes occasions, passepoil d’or.',
    tons: ['#1a0a1e', '#2e1233', '#4d1f52', '#7a3480', '#b06fb8'],
    extra: { d: '#c9922f', D: '#f5d99a', e: '#ecc33a', n: '#fff6c8', K: '#1a0a1e', L: '#2e1233', M: '#4d1f52' },
  },
  {
    id: 'tenue-nacre', name: 'Tenue de nacre',
    desc: 'Irisée comme l’intérieur d’un coquillage remonté du fond.',
    tons: ['#2a2438', '#453c5c', '#6d6390', '#a89ec4', '#eee6f5'],
    extra: { d: '#a89ec4', D: '#eee6f5', e: '#8fe6d8', l: '#2f5f5c', m: '#4f9e94', n: '#d8fff6' },
  },
  {
    id: 'tenue-prisme', name: 'Tenue de prisme',
    desc: 'Chaque pli renvoie une couleur différente. La Mine n’en donne pas deux.',
    // ⚠️ La seule tenue où les cinq tons changent de TEINTE et pas seulement de clarté : le relief
    // du héros la décompose alors comme un prisme. Le dégradé de luminance reste croissant, sans
    // quoi le personnage se lirait en négatif.
    tons: ['#1a0f2e', '#2d2a80', '#1f7f8f', '#4fbf5a', '#ffe27a'],
    extra: { d: '#4fbf5a', D: '#ffe27a', e: '#ff6fae', l: '#7a1f52', m: '#c23f86', n: '#ffd0e6', K: '#1a0f2e', L: '#2d2a80', M: '#1f7f8f' },
  },
]

const PAR_ID = new Map(OUTFITS.map((o) => [o.id, o]))

/** La tenue d'identifiant `id`, ou undefined (id vide, inconnu, ou tenue d'origine). */
export function outfitById(id: string): Outfit | undefined {
  return PAR_ID.get(id)
}

/**
 * Table complète lettre -> couleur pour cette tenue : les huit lettres du costume traduites par
 * TON_PAR_LETTRE, plus les retouches. Le résultat se fusionne par-dessus la palette d'origine.
 */
export function outfitCols(o: Outfit): Record<string, string> {
  const cols: Record<string, string> = {}
  for (const l of OUTFIT_LETTERS) cols[l] = o.tons[TON_PAR_LETTRE[l]]!
  if (o.extra) for (const k in o.extra) cols[k] = o.extra[k]!
  return cols
}
